import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Clock, Trophy, Play, Coins, CheckCircle2, MessageSquare, Info, DollarSign, Ticket, Sparkles, Award } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { useWallet, fmtUsd } from "@/lib/wallet";
import WalletBadge from "@/components/WalletBadge";
import RoomJoinModal from "@/components/RoomJoinModal";
import RoomLobbyModal from "@/components/RoomLobbyModal";
import PushToggle from "@/components/PushToggle";
import HowItWorksModal from "@/components/HowItWorksModal";
import LanguageSelector from "@/components/LanguageSelector";
import { FEATURES } from "@/lib/features";

const API = process.env.REACT_APP_BACKEND_URL;
const TIER_COLORS = {
  t_1:  "border-slate-500/60  text-slate-300",
  t_2:  "border-cyan-400/70   text-cyan-300",
  t_5:  "border-emerald-400/70 text-emerald-300",
  t_10: "border-purple-400/70  text-purple-300",
  t_15: "border-amber-300/80   text-amber-200",
};

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), intervalMs); return () => clearInterval(t); }, [intervalMs]);
  return now;
}

function fmtCountdown(ms, tNow) {
  if (ms <= 0) return tNow || "NOW";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m ${sec.toString().padStart(2, "0")}s`;
  if (m > 0) return `${m}m ${sec.toString().padStart(2, "0")}s`;
  return `${sec}s`;
}

export default function Rooms() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth();
  const { balance, refresh: refreshWallet } = useWallet(user ? 15000 : 0);
  const [rooms, setRooms] = useState([]);
  const [myParticipations, setMyParticipations] = useState({}); // room_id → participation
  const [joinTarget, setJoinTarget] = useState(null);
  const [lobbyTarget, setLobbyTarget] = useState(null);
  const [howOpen, setHowOpen] = useState(false);
  const [category, setCategory] = useState(FEATURES.paidRooms ? "paid" : "ticket"); // paid | ticket | tournament
  const [topEarners, setTopEarners] = useState([]);
  const now = useNow(1000);

  const loadRooms = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/rooms/upcoming`);
      if (r.ok) setRooms((await r.json()).rooms || []);
    } catch { /* ignore */ }
  }, []);

  const loadTopEarners = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/rooms/top-earners?days=30&limit=5`);
      if (r.ok) setTopEarners((await r.json()).earners || []);
    } catch { /* ignore */ }
  }, []);

  const loadParticipations = useCallback(async () => {
    if (!user) { setMyParticipations({}); return; }
    try {
      const r = await fetch(`${API}/api/rooms/me/active`, { credentials: "include" });
      if (!r.ok) return;
      const d = await r.json();
      const map = {};
      for (const p of (d.active || [])) map[p.room_id] = p;
      setMyParticipations(map);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { loadRooms(); const t = setInterval(loadRooms, 20000); return () => clearInterval(t); }, [loadRooms]);
  useEffect(() => { loadParticipations(); const t = setInterval(loadParticipations, 15000); return () => clearInterval(t); }, [loadParticipations]);
  useEffect(() => { loadTopEarners(); const t = setInterval(loadTopEarners, 60000); return () => clearInterval(t); }, [loadTopEarners]);

  const enrichedRooms = useMemo(() => rooms.map((r) => {
    const start = new Date(r.scheduled_start);
    const end = new Date(r.scheduled_end);
    const msToStart = start - now;
    const msToEnd = end - now;
    let phase;
    if (msToStart > 0) phase = "upcoming";
    else if (msToEnd > 0) phase = "active";
    else phase = "ended";
    return { ...r, start, end, msToStart, msToEnd, phase };
  }), [rooms, now]);

  const categorized = useMemo(() => {
    const active = enrichedRooms.filter((r) => r.phase !== "ended");
    return {
      paid: FEATURES.paidRooms ? active.filter((r) => r.entry_type !== "ticket" && r.entry_type !== "tournament") : [],
      ticket: active.filter((r) => r.entry_type === "ticket"),
      tournament: active.filter((r) => r.entry_type === "tournament"),
    };
  }, [enrichedRooms]);

  const visibleRooms = categorized[category] || [];

  const CATEGORIES = [
    ...(FEATURES.paidRooms ? [{ id: "paid", Icon: DollarSign, cls: "border-emerald-400 text-emerald-200 bg-emerald-500/10",  activeCls: "bg-emerald-500 text-black border-emerald-500" }] : []),
    { id: "ticket",     Icon: Ticket,     cls: "border-amber-300 text-amber-200 bg-amber-500/10",       activeCls: "bg-amber-400 text-black border-amber-400" },
    { id: "tournament", Icon: Sparkles,   cls: "border-cyan-400 text-cyan-200 bg-cyan-500/10",           activeCls: "bg-cyan-500 text-black border-cyan-500" },
  ];

  const onJoined = (result) => {
    setJoinTarget(null);
    refreshWallet();
    loadRooms();
    loadParticipations();
  };

  return (
    <div className="fixed inset-0 bg-[#050810] text-white overflow-auto" data-testid="rooms-page">
      {/* Depth layers — solid dark base + subtle radial gradient + starfield + scanlines + ambient blur orbs */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#08111e] via-[#050810] to-black" />
      <div className="pointer-events-none absolute inset-0 starfield opacity-25" />
      <div className="pointer-events-none absolute inset-0 scanlines opacity-70" />
      {/* Ambient blur orbs — repeat every ~900px so they cover the whole scroll length */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
      <div className="pointer-events-none absolute top-[600px] right-[-120px] h-96 w-96 rounded-full bg-secondary/12 blur-3xl" />
      <div className="pointer-events-none absolute top-[1200px] left-[-120px] h-96 w-96 rounded-full bg-cyan-400/10 blur-3xl" />
      <div className="pointer-events-none absolute top-[1800px] right-[-100px] h-96 w-96 rounded-full bg-primary/12 blur-3xl" />
      <div className="pointer-events-none absolute top-[2400px] left-[20%] h-96 w-96 rounded-full bg-amber-300/8 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 h-96 w-[80%] rounded-full bg-primary/8 blur-3xl" />

      <div className="relative max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => nav("/")} data-testid="rooms-back" className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-primary/40 bg-black/60 hover:bg-primary/10 text-primary font-mono text-[10px] tracking-widest">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("rooms.back")}
          </button>
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/pilot")} data-testid="rooms-to-pilot" className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-secondary/50 bg-black/60 hover:bg-secondary/10 text-secondary font-mono text-[10px] tracking-widest">
              <Play className="h-3.5 w-3.5" /> {t("rooms.go_play")}
            </button>
            <button onClick={() => setHowOpen(true)} data-testid="how-it-works-open"
              className="inline-flex items-center gap-1.5 rounded-sm border border-cyan-400/50 bg-black/60 hover:bg-cyan-500/10 text-cyan-200 font-mono tracking-widest text-[10px] px-3 py-2">
              <Info className="h-3.5 w-3.5" /> {t("rooms.how_it_works")}
            </button>
            <PushToggle />
            <WalletBadge />
            <LanguageSelector />
          </div>
        </div>

        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] text-secondary">{t("rooms.arena_label")}</p>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">{t("rooms.title")}</h1>
          <p className="text-sm text-muted-foreground max-w-2xl mt-2">
            {t("rooms.subtitle")}
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-3" data-testid="rooms-category-tabs">
          {CATEGORIES.map(({ id, Icon, cls, activeCls }) => {
            const count = (categorized[id] || []).length;
            const isActive = category === id;
            const subKey = id === "paid" ? "usdt" : id === "ticket" ? "ticket_sub" : "free";
            return (
              <button
                key={id}
                onClick={() => setCategory(id)}
                data-testid={`rooms-tab-${id}`}
                className={`inline-flex items-center gap-2.5 px-4 py-2.5 rounded-sm border-2 font-mono tracking-widest text-xs transition ${isActive ? activeCls : cls} hover:brightness-110`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-black">{t(`rooms.categories.${id}`)}</span>
                <span className={`text-[10px] opacity-70`}>· {t(`rooms.categories.${subKey}`)}</span>
                <span className={`ml-1 min-w-[22px] text-center px-1.5 py-0.5 rounded-sm text-[10px] font-black ${isActive ? "bg-black/30 text-current" : "bg-black/50 text-white"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* Rooms grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleRooms.slice(0, 24).map((r) => {
            const tier = TIER_COLORS[r.tier_id] || "border-primary/40 text-primary";
            const isTicket = r.entry_type === "ticket";
            const isTournament = r.entry_type === "tournament";
            const grossPool = isTicket || isTournament
              ? (r.prize_pool_cents || 0)
              : r.entry_fee_cents * r.capacity;
            // Paid rooms: platform keeps 10% (HOUSE_CUT_RATIO), 90% goes to the winners' pool.
            // 1st place = 25% of winners' pool (see backend rooms_catalog.WINNER_RATIOS).
            const potential = (isTicket || isTournament) ? grossPool : Math.round(grossPool * 0.9);
            const winnerPool = potential;
            const firstPrize = Math.round(winnerPool * 0.20);
            const full = r.player_count >= r.capacity;
            const started = r.phase === "active";
            const badges = [
              isTournament
                ? { key: "tournament", label: t("rooms.badges.tournament_free"), cls: "border-cyan-400 text-cyan-200 bg-cyan-500/10" }
                : isTicket
                  ? { key: "free",     label: t("rooms.badges.free_ticket"),     cls: "border-amber-300 text-amber-200 bg-amber-500/10" }
                  : { key: "paid",     label: t("rooms.badges.paga"),            cls: "border-emerald-400 text-emerald-200 bg-emerald-500/10" },
              started ? { key: "live", label: t("rooms.badges.live"),  cls: "border-red-400 text-red-200 bg-red-500/10 animate-pulse" } : null,
              full   ? { key: "full",  label: t("rooms.badges.full"),   cls: "border-primary/40 text-muted-foreground bg-black/40" } : null,
            ].filter(Boolean);
            return (
              <div key={r.room_id} data-testid={`room-card-${r.room_id}`}
                className={`group relative overflow-hidden rounded-md border ${tier.split(" ")[0]} bg-gradient-to-br from-black/85 via-black/70 to-black/40 backdrop-blur p-4 space-y-3 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/20 hover:border-primary/70`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] tracking-widest text-muted-foreground truncate">{r.tier_id.toUpperCase()}</p>
                    <h3 className={`font-display font-black text-2xl tracking-tighter ${tier.split(" ")[1]}`}>{r.label}</h3>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {badges.map((b) => (
                      <span key={b.key} data-testid={`room-badge-${r.room_id}-${b.key}`}
                        className={`font-mono text-[9px] tracking-widest px-2 py-1 border rounded-sm ${b.cls}`}>
                        {b.label}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Prize pool highlight */}
                <div className="rounded-sm border border-secondary/30 bg-black/50 p-3 flex items-baseline justify-between">
                  <div>
                    <p className="font-mono text-[9px] tracking-widest text-muted-foreground inline-flex items-center gap-1">
                      {isTournament ? t("rooms.prize_pool_tickets") : t("rooms.prize_pool")}
                      {!isTicket && !isTournament && (
                        <button
                          type="button"
                          onClick={() => setHowOpen(true)}
                          title={t("rooms.prize_pool")}
                          data-testid={`fee-tooltip-${r.room_id}`}
                          className="text-cyan-300 hover:text-cyan-100">
                          <Info className="h-3 w-3" />
                        </button>
                      )}
                    </p>
                    <p className="font-display font-black text-xl tracking-tighter text-secondary">
                      {isTournament ? "20 🎟" : fmtUsd(potential)}
                    </p>
                    {!isTicket && !isTournament && (
                      <p className="font-mono text-[8px] text-muted-foreground">{t("rooms.net_gross", { gross: fmtUsd(grossPool) })}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[9px] tracking-widest text-muted-foreground">{t("rooms.first_place")}</p>
                    <p className="font-display font-bold text-base text-amber-200">
                      {isTournament ? "5 🎟" : fmtUsd(firstPrize)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 font-mono text-[10px]">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Users className="h-3.5 w-3.5 text-primary" />
                    <span className="text-white font-bold">{r.player_count}<span className="text-muted-foreground">/{r.capacity}</span></span>
                  </div>
                  <div className="flex items-center gap-1.5 justify-end">
                    <Clock className={`h-3.5 w-3.5 ${started ? "text-emerald-300" : "text-orange-300"}`} />
                    <span data-testid={`room-status-${r.room_id}`}
                      className={
                        started
                          ? "text-emerald-200 font-bold"
                          : r.msToStart < 60_000
                            ? "text-red-300 animate-pulse font-bold"
                            : "text-orange-200 font-bold"
                      }>
                      {started
                        ? <>{t("rooms.status_live")} · <span className="text-white/80">{t("rooms.status_ends", { time: fmtCountdown(r.msToEnd, t("room_start_banner.now")) })}</span></>
                        : <span className="text-white">{t("rooms.status_starts", { time: fmtCountdown(r.msToStart, t("room_start_banner.now")) })}</span>
                      }
                    </span>
                  </div>
                </div>

                <div className="border-t border-primary/10 pt-2 flex justify-between font-mono text-[10px] text-muted-foreground">
                  <span>{t("rooms.entry")}</span>
                  <span className="text-white font-bold">
                    {isTicket ? `${r.entry_ticket_count || 1} 🎟` : fmtUsd(r.entry_fee_cents)}
                  </span>
                </div>

                {myParticipations[r.room_id] ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => nav("/pilot")} data-testid={`room-play-${r.room_id}`}
                      className="inline-flex items-center justify-center gap-1 rounded-sm bg-emerald-500 text-black hover:bg-emerald-400 font-mono tracking-widest text-[10px] px-3 py-2">
                      <CheckCircle2 className="h-3 w-3" /> {t("rooms.go_play")}
                    </button>
                    <button onClick={() => setLobbyTarget(r)} data-testid={`room-lobby-${r.room_id}`}
                      className="inline-flex items-center justify-center gap-1 rounded-sm border border-secondary/60 bg-black/60 hover:bg-secondary/10 text-secondary font-mono tracking-widest text-[10px] px-3 py-2">
                      <MessageSquare className="h-3 w-3" /> LOBBY
                    </button>
                  </div>
                ) : r.phase === "active" ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button disabled data-testid={`room-locked-${r.room_id}`}
                      className="rounded-sm border border-primary/20 text-muted-foreground font-mono tracking-widest text-[10px] px-3 py-2 opacity-50">
                      {t("rooms.closed_btn")}
                    </button>
                    <button onClick={() => setLobbyTarget(r)} data-testid={`room-lobby-${r.room_id}`}
                      className="inline-flex items-center justify-center gap-1 rounded-sm border border-secondary/40 bg-black/60 hover:bg-secondary/10 text-secondary font-mono tracking-widest text-[10px] px-3 py-2">
                      <Trophy className="h-3 w-3" /> {t("rooms.ranking_btn")}
                    </button>
                  </div>
                ) : full ? (
                  <button disabled data-testid={`room-full-${r.room_id}`}
                    className="w-full rounded-sm border border-primary/20 text-muted-foreground font-mono tracking-widest text-[10px] px-3 py-2 opacity-50">
                    {t("rooms.badges.full")}
                  </button>
                ) : isTournament ? (
                  <button onClick={() => setJoinTarget(r)} data-testid={`room-enter-${r.room_id}`}
                    className="w-full inline-flex items-center justify-center gap-1 rounded-sm bg-cyan-500 text-black hover:bg-cyan-400 font-mono tracking-widest text-[10px] px-3 py-2">
                    <Trophy className="h-3 w-3" /> {t("rooms.enter_btn")} · {t("rooms.categories.free")}
                  </button>
                ) : (
                  <button onClick={() => setJoinTarget(r)} data-testid={`room-enter-${r.room_id}`}
                    className={`w-full inline-flex items-center justify-center gap-1 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono tracking-widest text-[10px] px-3 py-2`}>
                    <Coins className="h-3 w-3" /> {t("rooms.enter_btn")} · {isTicket ? `${r.entry_ticket_count || 1} 🎟` : fmtUsd(r.entry_fee_cents)}
                  </button>
                )}
              </div>
            );
          })}
          {visibleRooms.length === 0 && (
            <div className="col-span-3 text-center font-mono text-sm text-muted-foreground py-16 border border-dashed border-primary/20 rounded-sm">
              {enrichedRooms.length === 0 ? t("rooms.loading") : t(`rooms.empty_${category}`)}
            </div>
          )}
        </div>

        {/* Maiores Ganhadores do Mês */}
        <div className="pt-6 space-y-4" data-testid="top-earners-section">
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-amber-200/80">{t("rooms.top_earners_label")}</p>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white inline-flex items-center gap-3">
                <Award className="h-8 w-8 text-amber-300" /> {t("rooms.top_earners_title")}
              </h2>
            </div>
          </div>
          {topEarners.length === 0 ? (
            <div className="rounded-sm border border-dashed border-amber-300/20 bg-black/40 py-10 text-center font-mono text-xs tracking-widest text-muted-foreground">
              {t("rooms.top_earners_empty")}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {topEarners.map((p, i) => {
                const initials = (p.display_name || "P").slice(0, 2).toUpperCase();
                const rankCls = i === 0 ? "border-amber-300/60 bg-gradient-to-b from-amber-500/20 to-transparent"
                             : i === 1 ? "border-slate-300/50 bg-gradient-to-b from-slate-400/15 to-transparent"
                             : i === 2 ? "border-orange-400/50 bg-gradient-to-b from-orange-500/15 to-transparent"
                             : "border-primary/20 bg-black/40";
                const rankText = i === 0 ? "text-amber-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-300" : "text-muted-foreground";
                return (
                  <div key={p.user_id} data-testid={`top-earner-${i}`}
                    className={`rounded-sm border ${rankCls} p-4 flex items-center gap-3`}>
                    <div className="relative shrink-0">
                      <div className="h-11 w-11 rounded-full bg-black/60 border border-primary/40 flex items-center justify-center font-display font-black text-primary text-sm">{initials}</div>
                      <span className={`absolute -top-1.5 -left-1.5 font-mono text-[9px] font-black tracking-widest px-1.5 py-0.5 rounded-sm bg-black border ${rankCls.split(" ")[0]} ${rankText}`}>#{i + 1}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display font-black text-base truncate text-white">{p.display_name}</p>
                      <p className="font-mono text-[9px] tracking-widest text-muted-foreground">{p.wins} {p.wins === 1 ? t("rooms.top_earners_prize") : t("rooms.top_earners_prizes")}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display font-black text-lg text-emerald-300 leading-none">{fmtUsd(p.total_cents)}</p>
                      <p className="font-mono text-[9px] tracking-widest text-emerald-200/60 mt-0.5">USDT</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <RoomJoinModal open={!!joinTarget} room={joinTarget} onClose={() => setJoinTarget(null)} onJoined={onJoined} balance={balance} />
      <RoomLobbyModal open={!!lobbyTarget} room={lobbyTarget}
        myParticipation={lobbyTarget ? myParticipations[lobbyTarget.room_id] : null}
        onClose={() => setLobbyTarget(null)} />
      <HowItWorksModal open={howOpen} onClose={() => setHowOpen(false)} />
    </div>
  );
}
